import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { RevealText } from "./RevealText";
import { IMAGES } from "../../lib/landing";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 sm:pt-32 pb-16"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 h-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end min-h-[calc(100svh-11rem)]">
          {/* Left: massive headline */}
          <motion.div style={{ y: textY }} className="lg:col-span-7 pt-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 1 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-brand-primary" />
              <span className="text-xs uppercase tracking-[0.25em] text-brand-muted">
                Sicurezza · Prevenzione · Empowerment
              </span>
            </motion.div>

            <h1 className="font-display font-medium tracking-tight leading-[0.92] text-brand-text text-[15vw] sm:text-[11vw] lg:text-[7.4vw]">
              <RevealText lines={["Nessuna donna", "dovrebbe sentirsi"]} delay={0.35} />
              <span className="line-mask">
                <motion.span
                  className="block will-change-transform italic text-brand-primary font-serif-accent"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.53 }}
                >
                  mai più sola.
                </motion.span>
              </span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.9 }}
              className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
            >
              <p className="max-w-md text-brand-muted text-base md:text-lg leading-relaxed">
                Safe(H)er è l'app che unisce tecnologia, prevenzione e una
                community reale per proteggere le donne, ogni giorno e ovunque.
              </p>
              <a
                href="#scarica"
                data-testid="hero-download-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-inverse px-6 py-4 text-sm shrink-0 hover:bg-brand-primaryhover transition-colors duration-300 active:scale-95"
              >
                Scarica l'app
                <ArrowUpRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </motion.div>
          </motion.div>

          {/* Right: parallax portrait */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
              className="relative aspect-[3/4] w-full overflow-hidden rounded-[2px]"
            >
              <motion.img
                src={IMAGES.heroPortrait}
                alt="Donna sicura di sé"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-[115%] w-full object-cover grayscale-[15%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="absolute -bottom-5 -left-5 sm:left-auto sm:-right-5 bg-brand-bg border border-brand-line rounded-2xl px-5 py-4 shadow-[0_20px_50px_-30px_rgba(28,25,23,0.5)]"
            >
              <p className="font-display text-3xl text-brand-text leading-none">
                24/7
              </p>
              <p className="text-xs text-brand-muted mt-1">
                Protezione attiva
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.a
        href="#manifesto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="hidden lg:flex items-center gap-2 absolute bottom-8 left-8 text-xs uppercase tracking-[0.2em] text-brand-muted hover:text-brand-text transition-colors"
        data-testid="scroll-cue"
      >
        <ArrowDownRight size={16} className="animate-pulse" />
        Scorri per scoprire
      </motion.a>
    </section>
  );
};

export default Hero;
